'use client';

/**
 * SubscriptionsTabConnected — the RevOps admin "Subscriptions" tab, wired to the real
 * backend (/api/platform/v1/revops/subscriptions) via platformSubscriptions API client,
 * replacing the old localStorage-backed SubscriptionsTab + hand-typed-MRR modals.
 *
 * - Lists real ro_subscriptions and renders the (previously unused) SubscriptionManager.
 * - Create: pick an org + a plan tier + seats; MRR is DERIVED server-side (never typed).
 * - Change plan / cancel / pause / resume: call the real lifecycle endpoints.
 * - Import from Pipeline: turn a won deal into a real subscription on a chosen tier
 *   (tagged with the opportunity), which also provisions the tier's capabilities.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X, DownloadCloud } from 'lucide-react';
import SubscriptionManager from './SubscriptionManager/SubscriptionManager';
import type { Subscription, PricingPlan, Customer, MRRSummary } from '../types';
import { useRevOps, type PipelineOpportunity } from '@/contexts/RevOpsContext';
import {
  listSubscriptions,
  listPlanTiers,
  listCustomers,
  calculateTier,
  createSubscription,
  changePlan,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  deriveMrrSummary,
} from '@/services/api/platformSubscriptions';

const EMPTY_MRR: MRRSummary = {
  totalMrr: 0, totalArr: 0, totalCustomers: 0, totalSubscriptions: 0,
  newMrr: 0, expansionMrr: 0, contractionMrr: 0, churnMrr: 0, reactivationMrr: 0, netMrrChange: 0,
};

export default function SubscriptionsTabConnected() {
  const { organizations, wonDeals } = useRevOps();

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState<Subscription | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subs, tiers, custs] = await Promise.all([
        listSubscriptions(),
        listPlanTiers(),
        listCustomers().catch(() => [] as Customer[]),
      ]);
      setSubscriptions(subs);
      setPlans(tiers);
      setCustomers(custs);
    } catch (e: any) {
      setError(e?.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const plansRecord = useMemo(() => {
    const rec: Record<string, PricingPlan> = {};
    plans.forEach((p) => { rec[p.id] = p; });
    return rec;
  }, [plans]);

  const customersRecord = useMemo(() => {
    const rec: Record<string, Customer> = {};
    customers.forEach((c) => { rec[c.id] = c; });
    return rec;
  }, [customers]);

  const mrrSummary = useMemo(
    () => (subscriptions.length ? deriveMrrSummary(subscriptions, customers.length) : EMPTY_MRR),
    [subscriptions, customers.length],
  );

  const runAction = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.message || 'Action failed');
    }
  }, [load]);

  // Won deals not yet turned into a subscription (by opportunityId).
  const existingOppIds = useMemo(
    () => new Set(subscriptions.map((s) => s.opportunityId).filter(Boolean) as string[]),
    [subscriptions],
  );
  const unconvertedDeals = useMemo(
    () => (wonDeals || []).filter((d) => !existingOppIds.has(d.id)),
    [wonDeals, existingOppIds],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading subscriptions…
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {unconvertedDeals.length > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100"
          >
            <DownloadCloud className="h-4 w-4" /> Import from Pipeline ({unconvertedDeals.length})
          </button>
        </div>
      )}

      <SubscriptionManager
        subscriptions={subscriptions}
        customers={customersRecord}
        plans={plansRecord}
        mrrSummary={mrrSummary}
        loading={loading}
        onSubscriptionClick={(s) => setChangeTarget(s)}
        onChangePlan={(id) => {
          const s = subscriptions.find((x) => x.id === id) || null;
          setChangeTarget(s);
        }}
        onCancel={(id) => runAction(() => cancelSubscription(id))}
        onPause={(id) => runAction(() => pauseSubscription(id))}
        onResume={(id) => runAction(() => resumeSubscription(id))}
        onCreateSubscription={() => setCreateOpen(true)}
      />

      {createOpen && (
        <SubscriptionFormModal
          title="New Subscription"
          plans={plans}
          organizations={organizations}
          onClose={() => setCreateOpen(false)}
          onSubmit={async ({ cboId, tierId, seats }) => {
            await createSubscription({ cbo_id: cboId, plan_tier_id: tierId, quantity: seats });
            setCreateOpen(false);
            await load();
          }}
        />
      )}

      {changeTarget && (
        <SubscriptionFormModal
          title={`Change Plan — ${changeTarget.subscriptionNumber}`}
          plans={plans}
          organizations={organizations}
          lockedOrg
          defaultSeats={changeTarget.quantity}
          onClose={() => setChangeTarget(null)}
          onSubmit={async ({ tierId, seats }) => {
            await changePlan(changeTarget.id, tierId, seats);
            setChangeTarget(null);
            await load();
          }}
        />
      )}

      {importOpen && (
        <ImportFromPipelineModal
          deals={unconvertedDeals}
          plans={plans}
          onClose={() => setImportOpen(false)}
          onSubmit={async ({ deal, tierId }) => {
            if (!deal.companyId) throw new Error('This deal has no linked organization (companyId).');
            await createSubscription({
              cbo_id: deal.companyId,
              plan_tier_id: tierId,
              opportunity_id: deal.id,
            });
            setImportOpen(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Create / Change-plan modal — tier picker + server-derived MRR (never typed)
// -----------------------------------------------------------------------------

function SubscriptionFormModal(props: {
  title: string;
  plans: PricingPlan[];
  organizations: { id: number; name: string }[];
  lockedOrg?: boolean;
  defaultSeats?: number;
  onClose: () => void;
  onSubmit: (v: { cboId: number; tierId: string; seats: number }) => Promise<void>;
}) {
  const { title, plans, organizations, lockedOrg, defaultSeats, onClose, onSubmit } = props;
  const [cboId, setCboId] = useState<number | undefined>(lockedOrg ? -1 : undefined);
  const [tierId, setTierId] = useState<string>(plans[0]?.id || '');
  const [seats, setSeats] = useState<number>(defaultSeats || 1);
  const [derivedMrr, setDerivedMrr] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!tierId) { setDerivedMrr(null); return; }
    calculateTier(tierId, seats)
      .then((r) => { if (!cancelled) setDerivedMrr(r.subtotal ?? null); })
      .catch(() => { if (!cancelled) setDerivedMrr(null); });
    return () => { cancelled = true; };
  }, [tierId, seats]);

  const submit = async () => {
    setErr(null);
    if (!lockedOrg && !cboId) { setErr('Select an organization'); return; }
    if (!tierId) { setErr('Select a plan tier'); return; }
    setSaving(true);
    try {
      await onSubmit({ cboId: cboId as number, tierId, seats });
    } catch (e: any) {
      setErr(e?.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      {!lockedOrg && (
        <label className="block mb-3">
          <span className="text-sm font-medium text-gray-700">Organization</span>
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={cboId ?? ''}
            onChange={(e) => setCboId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Select an organization…</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
      )}

      <label className="block mb-3">
        <span className="text-sm font-medium text-gray-700">Plan tier</span>
        <select
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={tierId}
          onChange={(e) => setTierId(e.target.value)}
        >
          {plans.length === 0 && <option value="">No plan tiers configured</option>}
          {plans.map((p) => (
            <option key={p.id} value={p.id}>{p.planName}</option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-gray-400">
          Pricing & capabilities come from the Pricing tab. Configure tiers there.
        </span>
      </label>

      <label className="block mb-3">
        <span className="text-sm font-medium text-gray-700">Seats</span>
        <input
          type="number"
          min={1}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={seats}
          onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
        />
      </label>

      <div className="mb-4 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
        <span className="text-gray-500">Derived MRR:</span>{' '}
        <span className="font-semibold text-gray-900">
          {derivedMrr === null ? '—' : `$${derivedMrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`}
        </span>
        <span className="ml-2 text-xs text-gray-400">(computed from the tier — not editable)</span>
      </div>

      {err && <div className="mb-3 text-sm text-red-600">{err}</div>}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">Cancel</button>
        <button
          onClick={submit}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Import-from-pipeline modal
// -----------------------------------------------------------------------------

function ImportFromPipelineModal(props: {
  deals: PipelineOpportunity[];
  plans: PricingPlan[];
  onClose: () => void;
  onSubmit: (v: { deal: PipelineOpportunity; tierId: string }) => Promise<void>;
}) {
  const { deals, plans, onClose, onSubmit } = props;
  const [dealId, setDealId] = useState<string>(deals[0]?.id || '');
  const [tierId, setTierId] = useState<string>(plans[0]?.id || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const deal = deals.find((d) => d.id === dealId);

  const submit = async () => {
    setErr(null);
    if (!deal) { setErr('Select a won deal'); return; }
    if (!tierId) { setErr('Select a plan tier'); return; }
    setSaving(true);
    try {
      await onSubmit({ deal, tierId });
    } catch (e: any) {
      setErr(e?.message || 'Failed to import');
      setSaving(false);
    }
  };

  return (
    <Modal title="Import from Pipeline" onClose={onClose}>
      <p className="mb-3 text-sm text-gray-500">
        Turn a won deal into a real subscription on a plan tier. Pricing and capabilities
        come from the tier — no hand-typed MRR.
      </p>
      <label className="block mb-3">
        <span className="text-sm font-medium text-gray-700">Won deal</span>
        <select
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={dealId}
          onChange={(e) => setDealId(e.target.value)}
        >
          {deals.map((d) => (
            <option key={d.id} value={d.id}>{d.company} — {d.name}</option>
          ))}
        </select>
      </label>
      {deal && !deal.companyId && (
        <div className="mb-3 text-sm text-amber-600">
          This deal has no linked organization; it cannot be imported.
        </div>
      )}
      <label className="block mb-4">
        <span className="text-sm font-medium text-gray-700">Plan tier</span>
        <select
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={tierId}
          onChange={(e) => setTierId(e.target.value)}
        >
          {plans.map((p) => (
            <option key={p.id} value={p.id}>{p.planName}</option>
          ))}
        </select>
      </label>
      {err && <div className="mb-3 text-sm text-red-600">{err}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">Cancel</button>
        <button
          onClick={submit}
          disabled={saving || (deal && !deal.companyId)}
          className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Convert to Subscription
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-4 py-4">{children}</div>
      </div>
    </div>
  );
}
